import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import DefaultLayout from 'admin/components/DefaultLayout';
import { useState, useEffect } from 'react';
import { DrawerChat } from 'ui/Drawer/Drawers/DrawerChat';
import DrawerSendMessage from 'ui/Drawer/Drawers/DrawerSendMessage';
import { ChatConversations } from 'admin/components/Chat';
import useQueryApiClient from 'utils/useQueryApiClient';
import { Tabs } from 'ui';
import { StyledMobileHeaderWrapper } from 'admin/components/MobileLayout/style';
import { StyledChatPage } from './style';

export const ChatPage = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reload, setReload] = useState(0);
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [sendMessageDrawerOpen, setSendMessageDrawerOpen] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [selectedOtherFarmId, setSelectedOtherFarmId] = useState<number | null>(
    null
  );
  const [listingContext, setListingContext] = useState<{
    listingId: string;
    recipientFarmId: number;
    listingTitle?: string;
    listingIdentifier?: string;
  } | null>(null);

  const listingId = searchParams.get('listing_id');
  const recipientFarmIdParam = searchParams.get('recipient_farm_id');
  const conversationIdParam = searchParams.get('conversation_id');
  const listingTitle = searchParams.get('listing_title');
  const listingIdentifier = searchParams.get('listing_identifier');

  const { appendData: fetchConversationByListing } = useQueryApiClient({
    request: {
      url: '/api/v2/messages/conversations/by-listing',
      disableOnMount: true,
    },
    onSuccess: (response: {
      conversation_id?: string | null;
      recipient_farm?: { id: number; name: string; brand?: string } | null;
    }) => {
      if (response?.conversation_id && response?.recipient_farm) {
        setSelectedConversationId(response.conversation_id);
        setSelectedOtherFarmId(response.recipient_farm.id);
        setChatDrawerOpen(true);
      } else if (listingId && recipientFarmIdParam) {
        setListingContext({
          listingId,
          recipientFarmId: parseInt(recipientFarmIdParam, 10),
          listingTitle: listingTitle ?? undefined,
          listingIdentifier: listingIdentifier ?? undefined,
        });
        setSendMessageDrawerOpen(true);
      }
      setSearchParams({}, { replace: true });
    },
  });

  useEffect(() => {
    if (conversationIdParam && recipientFarmIdParam) {
      setSelectedConversationId(conversationIdParam);
      setSelectedOtherFarmId(parseInt(recipientFarmIdParam, 10));
      setChatDrawerOpen(true);
      setSearchParams({}, { replace: true });
    } else if (listingId && recipientFarmIdParam) {
      fetchConversationByListing({
        listing_id: listingId,
        recipient_farm_id: recipientFarmIdParam,
      });
    }
  }, [conversationIdParam, listingId, recipientFarmIdParam]);

  useEffect(() => {
    const recipientIdParam = searchParams.get('recipientid');
    if (recipientIdParam) {
      const recipientId = parseInt(recipientIdParam, 10);
      if (!isNaN(recipientId) && recipientId > 0) {
        setSelectedOtherFarmId(recipientId);
        setChatDrawerOpen(true);
      }
    }
  }, [searchParams]);

  const items = [
    {
      key: 'conversations',
      label: intl.formatMessage({ id: 'navigation.chat' }),
      children: (
        <ChatConversations
          reload={reload}
          onConversationClick={({ conversationId, otherFarmId }) => {
            setSelectedConversationId(conversationId);
            setSelectedOtherFarmId(otherFarmId);
            setChatDrawerOpen(true);
          }}
        />
      ),
    },
  ];

  return (
    <>
      <StyledMobileHeaderWrapper />
      <DefaultLayout.PageLayout
        breadcrumbs={[
          {
            title: intl.formatMessage({ id: 'navigation.beginning' }),
            href: '/',
          },
          {
            title: intl.formatMessage({ id: 'navigation.chat' }),
          },
        ]}
      >
        <DefaultLayout.PageContent>
          <StyledChatPage>
            <Tabs items={items} />
          </StyledChatPage>
        </DefaultLayout.PageContent>

        <DrawerChat
          open={chatDrawerOpen}
          conversationId={selectedConversationId}
          recipientFarmId={selectedOtherFarmId}
          onClose={() => {
            setChatDrawerOpen(false);
            setSelectedConversationId(null);
            setSelectedOtherFarmId(null);
            setReload((prev) => prev + 1);

            if (searchParams.has('recipientid')) {
              searchParams.delete('recipientid');
              setSearchParams(searchParams, { replace: true });
            }
          }}
        />

        <DrawerSendMessage
          open={sendMessageDrawerOpen}
          onClose={() => {
            setSendMessageDrawerOpen(false);
            setListingContext(null);
            setReload((prev) => prev + 1);
          }}
          onSuccess={() => setReload((prev) => prev + 1)}
          context={
            listingContext
              ? {
                  type: 'listing' as const,
                  listingId: listingContext.listingId,
                  preselectedRecipients: [listingContext.recipientFarmId],
                  listingTitle: listingContext.listingTitle,
                  listingIdentifier: listingContext.listingIdentifier,
                }
              : undefined
          }
        />
      </DefaultLayout.PageLayout>
    </>
  );
};
